import React, {useState, useMemo} from 'react'
import {Accordion, Button, Modal} from 'react-daisyui'
import {formatDateRange, formatDayAndDateTime} from '../../services/utils'
import {
  useGetMemberReservationQuery,
  useGetReservationInvoiceQuery
} from '../../services/guestPortalApi'
import {
  useGetPaymentPlanQuery,
  useGetPaymentScheduleQuery,
  useGetPaymentHistoryQuery,
  useGetBillingPeriodsQuery
} from '../../services/paymentInstallmentsApi'
import {skipToken} from '@reduxjs/toolkit/query'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {
  faChevronRight,
  faArrowUpRightFromSquare
} from '@fortawesome/free-solid-svg-icons'
import MemberUpdatePayment from './MemberUpdatePayment'
import CancellationPolicy from './CancellationPolicy'
import LoadingSpinner, {LoadingSpinnerSize} from '../common/LoadingSpinner'
import {Link} from 'react-router-dom'
import CancelReservationForm from '../account/CancelReservationForm'
import ModifyReservationForm from './ModifyReservationForm'
import PropertyContactCard from './PropertyContactCard'
import DownloadSection from './DownloadSection'
import ContactInfoFooter from './ContactInfoFooter'
import MultiInvoiceModal from './MultiInvoiceModal'
import PaymentInstallmentScheduleSection from './PaymentInstallmentScheduleSection'
import clsx from 'clsx'
import StaysEmptyState from '../../assets/checkin-room.png'
import HiltonWelcomeBanner from './HiltonWelcomeBanner'
import {trackBookingManagement} from '../../services/analytics'
import {trackClick} from '../../services/coreAnalytics'
import {getUser, getUserToken} from '../../services/token'
import {
  stripHtmlTags,
  downloadPdf,
  downloadAllPdfsSequentially
} from './reservationHelpers'
import {
  useAddBillingPeriodMutation,
  usePrepareReceiptMutation
} from '../../services/pdfProcessorApi'
import {useCancellationPolicyPopover} from '../../hooks/useCancellationPolicyPopover'
interface ManageReservationProps {
  reservationId: string
  propertiesData: any
  onStayModification?: Function
  isPastReservation?: boolean
}

const ManageReservation = ({
  reservationId,
  propertiesData,
  onStayModification,
  isPastReservation = false
}: ManageReservationProps) => {
  const reservationQuery = useGetMemberReservationQuery({reservationId})
  const propertyData = propertiesData[reservationQuery?.data?.propertyId]

  const invoiceQuery = useGetReservationInvoiceQuery(
    reservationQuery.data
      ? {
          reservationId: reservationQuery.data.reservationId
        }
      : skipToken
  )

  const paymentPlanQuery = useGetPaymentPlanQuery(
    reservationQuery.data
      ? {reservationId: reservationQuery.data.reservationId}
      : skipToken
  )

  const paymentScheduleQuery = useGetPaymentScheduleQuery(
    paymentPlanQuery.data?.hasPaymentSchedule
      ? {reservationId: reservationQuery.data?.reservationId}
      : skipToken
  )

  const billingPeriodsQuery = useGetBillingPeriodsQuery(
    paymentPlanQuery.data?.hasPaymentSchedule
      ? {reservationId: reservationQuery.data?.reservationId}
      : skipToken
  )

  const paymentHistoryQuery = useGetPaymentHistoryQuery(
    paymentPlanQuery.data?.hasPaymentSchedule
      ? {reservationId: reservationQuery.data?.reservationId}
      : skipToken
  )

  const isMonthlyStay = paymentPlanQuery.data?.hasPaymentSchedule || false

  const [addBillingPeriod] = useAddBillingPeriodMutation()
  const [prepareReceipt] = usePrepareReceiptMutation()

  const isHiltonReservation = (() => {
    const r = reservationQuery.data
    if (!r) {
      return false
    }
    return (
      r.isHilton === true ||
      (r.isHilton as unknown) === 'true' ||
      r.pmsSource === 'Hilton' ||
      r.channel === 'Hilton'
    )
  })()

  const prepareReceiptForFolio = async (folio: any): Promise<string> => {
    // Non-Hilton bookings have no Hilton rows to strip — skip the call.
    if (!isHiltonReservation) {
      return folio.invoicePreviewPdf
    }

    try {
      const result = await prepareReceipt({
        pdfBase64: folio.invoicePreviewPdf,
        ratePlanCode: reservationQuery.data?.ratePlanCode,
        totalAmount: reservationQuery.data?.totalAmount,
        folioId: folio.id
      }).unwrap()
      return result.pdfBase64 || folio.invoicePreviewPdf
    } catch (error) {
      console.error(
        `[PDF Download] Error preparing receipt for folio ${folio.id}:`,
        error
      )
      return folio.invoicePreviewPdf
    }
  }

  const foliosWithBillingPeriod = useMemo(() => {
    const folios = invoiceQuery.data?.folios || []
    const billingPeriods = billingPeriodsQuery.data?.billingPeriods || []

    const billingPeriodsByFolioId = billingPeriods.reduce(
      (acc: any, item: any) => {
        acc[item.folioId] = item.billingPeriod
        return acc
      },
      {}
    )

    return folios.map((folio: any) => {
      const apiBillingPeriod = billingPeriodsByFolioId[folio.id]
      return {
        ...folio,
        billingPeriod: apiBillingPeriod
      }
    })
  }, [invoiceQuery.data?.folios, billingPeriodsQuery.data?.billingPeriods])

  function getInstallmentLabel(index: number, total: number): string {
    if (index === total - 1) {
      return 'Final Installment'
    }
    const ordinals = [
      'First',
      'Second',
      'Third',
      'Fourth',
      'Fifth',
      'Sixth',
      'Seventh',
      'Eighth'
    ]
    return `${ordinals[index] || `${index + 1}th`} Installment`
  }

  const scheduleWithBillingPeriods = useMemo(() => {
    const schedule = paymentScheduleQuery.data?.schedule || []
    const billingPeriods = billingPeriodsQuery.data?.billingPeriods || []

    const billingPeriodsByFolioId = billingPeriods.reduce(
      (acc: any, item: any) => {
        acc[item.folioId] = item.billingPeriod
        return acc
      },
      {}
    )

    return schedule.map((item: any, index: number) => {
      const apiBillingPeriod = billingPeriodsByFolioId[item.folioId]

      const billingPeriod = apiBillingPeriod ||
        item.billingPeriod || {
          startDate: item.routing?.filter?.from || '',
          endDate: item.routing?.filter?.to || '',
          formattedPeriod: '',
          installmentLabel: getInstallmentLabel(index + 1, schedule.length + 1)
        }

      return {
        ...item,
        billingPeriod
      }
    })
  }, [
    paymentScheduleQuery.data?.schedule,
    billingPeriodsQuery.data?.billingPeriods
  ])

  const paymentHistoryWithBillingPeriods = useMemo(() => {
    const folioPayments = paymentHistoryQuery.data?.folioPayments || []
    const billingPeriods = billingPeriodsQuery.data?.billingPeriods || []

    const billingPeriodsByFolioId = billingPeriods.reduce(
      (acc: any, item: any) => {
        acc[item.folioId] = item.billingPeriod
        return acc
      },
      {}
    )

    return folioPayments.map((item: any, index: number) => {
      const apiBillingPeriod = billingPeriodsByFolioId[item.folioId]

      const billingPeriod = apiBillingPeriod ||
        item.billingPeriod || {
          startDate: '',
          endDate: '',
          formattedPeriod: '',
          installmentLabel: getInstallmentLabel(index, folioPayments.length)
        }

      return {
        ...item,
        billingPeriod
      }
    })
  }, [
    paymentHistoryQuery.data?.folioPayments,
    billingPeriodsQuery.data?.billingPeriods
  ])

  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showModifyDialog, setShowModifyDialog] = useState(false)
  const [openSection, setOpenSection] = useState<string | null>('details')

  const {Dialog} = Modal.useDialog()

  const receiptsLoading =
    invoiceQuery.isLoading ||
    paymentScheduleQuery.isLoading ||
    billingPeriodsQuery.isLoading ||
    paymentHistoryQuery.isLoading

  const {
    showPopover: showCancellationPolicyPopover,
    setShowPopover: setShowCancellationPolicyPopover,
    triggerRef: policyTriggerRef,
    popoverRef: policyPopoverRef,
    popoverPosition: policyPopoverPosition,
    updatePopoverPosition: updatePolicyPopoverPosition
  } = useCancellationPolicyPopover()

  const [showMultiInvoiceModal, setShowMultiInvoiceModal] = React.useState(
    false
  )
  const [pendingDownloadFolios, setPendingDownloadFolios] = React.useState<any>(
    []
  )
  const [downloadingInvoiceId, setDownloadingInvoiceId] = React.useState<
    string | null
  >(null)

  const handleDownloadAllInvoices = async () => {
    const token = getUserToken()
    const user = token ? getUser(token) : null
    trackClick(
      'download-invoice-button',
      `/member/stays/${reservationId}`,
      user?.email
    )

    const pdfFolios = foliosWithBillingPeriod.filter(
      (folio: any) => !!folio.invoicePreviewPdf
    )

    if (pdfFolios.length > 1) {
      setPendingDownloadFolios(pdfFolios)
      setShowMultiInvoiceModal(true)
    } else if (pdfFolios.length === 1) {
      await handleDownloadSingleInvoice(pdfFolios[0])
    }
  }

  const handleConfirmMultiDownload = async () => {
    const token = getUserToken()
    const user = token ? getUser(token) : null
    trackClick(
      'confirm-multi-download-button',
      `/member/stays/${reservationId}`,
      user?.email
    )

    setShowMultiInvoiceModal(false)

    const pdfList = await Promise.all(
      pendingDownloadFolios.map(async (folio: any) => {
        let pdfBase64 = await prepareReceiptForFolio(folio)

        if (isMonthlyStay && folio.billingPeriod) {
          try {
            const result = await addBillingPeriod({
              pdfBase64,
              billingPeriodStart: folio.billingPeriod.startDate,
              billingPeriodEnd: folio.billingPeriod.endDate,
              installmentLabel: folio.billingPeriod.installmentLabel,
              folioId: folio.id
            }).unwrap()
            pdfBase64 = result.pdfBase64 || pdfBase64
          } catch (error) {
            console.error(
              `[PDF Multi-Download] Error adding billing period to folio ${folio.id}:`,
              error
            )
          }
        }

        return {
          base64: pdfBase64,
          filename: `placemakr-invoice-${folio.id}.pdf`
        }
      })
    )

    if (pdfList.length > 0) {
      downloadAllPdfsSequentially(pdfList)
    }
  }

  const handleDownloadSingleInvoice = async (folio: any) => {
    if (!folio?.invoicePreviewPdf) {
      return
    }

    setDownloadingInvoiceId(folio.id)

    try {
      const filename = `placemakr-invoice-${folio.id}.pdf`
      let pdfBase64 = await prepareReceiptForFolio(folio)

      if (isMonthlyStay && folio.billingPeriod) {
        try {
          const result = await addBillingPeriod({
            pdfBase64,
            billingPeriodStart: folio.billingPeriod.startDate,
            billingPeriodEnd: folio.billingPeriod.endDate,
            installmentLabel: folio.billingPeriod.installmentLabel,
            folioId: folio.id
          }).unwrap()
          pdfBase64 = result.pdfBase64 || pdfBase64
        } catch (error) {
          console.error(
            '[PDF Download] Error adding billing period to PDF:',
            error
          )
        }
      }

      downloadPdf(pdfBase64, filename)
    } finally {
      setDownloadingInvoiceId(null)
    }
  }

  return (
    <main className="px-4 md:px-0">
      {reservationQuery.isError && (
        <div
          className="text-red-400"
          aria-label="Error loading reservation"
          aria-live="polite"
          aria-errormessage="Error loading reservation"
        >
          Error loading reservation
        </div>
      )}

      {reservationQuery.isLoading && (
        <LoadingSpinner size={LoadingSpinnerSize.Large} />
      )}

      {reservationQuery.data ? (
        <div className="flex flex-col min-h-[700px]">
          <Dialog open={showCancelDialog}>
            <CancelReservationForm
              reservation={reservationQuery.data}
              onCancel={() => {
                setShowCancelDialog(false)
                reservationQuery.refetch()
                if (onStayModification) {
                  onStayModification(reservationQuery.data)
                }
              }}
              onGoBack={() => {
                setShowCancelDialog(false)
              }}
            />
          </Dialog>
          {/* overflow-visible: let the date-picker calendar escape the
              modal-box, which otherwise clips it with overflow-y: auto.
              max-w-2xl: room for the recap's two-column money block */}
          <Dialog
            open={showModifyDialog}
            className="overflow-visible max-w-2xl"
          >
            <ModifyReservationForm
              reservation={reservationQuery.data}
              onDone={() => {
                setShowModifyDialog(false)
                reservationQuery.refetch()
                if (onStayModification) {
                  onStayModification(reservationQuery.data)
                }
              }}
              onGoBack={() => {
                setShowModifyDialog(false)
              }}
            />
          </Dialog>
          <div
            className="breadcrumbs text-md"
            aria-label="Breadcrumb navigation"
          >
            <ul>
              <li>
                <Link
                  to="/member/stays"
                  className="btn btn-sm btn-breadcrumb btn-link no-underline hover:no-underline font-bold text-cabana"
                  aria-label="Navigate to My Stays"
                >
                  My Stays
                </Link>
              </li>
              <FontAwesomeIcon
                icon={faChevronRight}
                className="faSolid pr-2 text-xs"
              />
              <span className="font-medium">
                {propertyData?.location}
                <span className={clsx('hidden', 'md:inline')}>
                  {', '}
                  {formatDateRange(
                    reservationQuery?.data?.checkinDate,
                    reservationQuery?.data?.checkoutDate
                  )}
                </span>
                <span className={clsx('inline', 'lg:hidden')}>
                  {', '}
                  {formatDateRange(
                    reservationQuery?.data?.checkinDate,
                    reservationQuery?.data?.checkoutDate
                  )}
                </span>
              </span>
            </ul>
          </div>
          {reservationQuery.data?.isHilton === true && <HiltonWelcomeBanner />}
          <section
            className={`collapse collapse-arrow ${
              openSection === 'details' ? 'collapse-open' : ''
            }`}
            aria-label="Reservation Details Section"
          >
            <input
              type="checkbox"
              name="res-accordion"
              aria-controls="reservation-details-accordion"
              checked={openSection === 'details'}
              onChange={() =>
                setOpenSection(openSection === 'details' ? null : 'details')
              }
            />
            <Accordion.Title className="accordion-title collapse-arrow text-2xl font-semibold">
              Reservation Details
            </Accordion.Title>
            <Accordion.Content className="accordion-content">
              <div className="w-full h-px bg-gray-200 mb-1 md:mb-4" />
              <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-10">
                {isPastReservation ? (
                  <>
                    <div className="flex flex-col items-start md:w-3/5 gap-2 md:gap-3">
                      <img
                        src={
                          propertyData?.images?.[0]?.image ?? StaysEmptyState
                        }
                        alt="Stay image"
                        className="rounded-lg w-full max-w-[160px] md:max-w-[180px] mb-2"
                      />
                      <div className="font-semibold text-lg mb-1">
                        This stay is in the past
                      </div>
                      <p className="text-sm text-gray-700 mb-2">
                        Your visit to {propertyData?.name} from{' '}
                        {formatDateRange(
                          reservationQuery?.data?.checkinDate,
                          reservationQuery?.data?.checkoutDate,
                          'to'
                        )}{' '}
                        is now part of your travel history — but it doesn't have
                        to end there. We'd love to welcome you back.
                      </p>
                      <Button
                        className="btn btn-secondary font-semibold w-fit"
                        onClick={() => {
                          const token = getUserToken()
                          const user = token ? getUser(token) : null
                          trackClick(
                            'new-search-button',
                            'https://www.placemakr.com/book#/booking/search',
                            user?.email
                          )

                          window.open(
                            'https://www.placemakr.com/book#/booking/search'
                          )
                        }}
                      >
                        start a new search
                      </Button>
                    </div>
                    <div className="my-4 w-full h-px bg-gray-200 md:hidden" />
                    <div
                      className="
                        hidden
                        md:block
                        md:mt-4
                        md:h-[360px]
                        md:w-px
                        md:bg-gray-200
                        md:rounded-full
                        self-start
                      "
                    />
                  </>
                ) : (
                  <div className="flex flex-col items-start md:w-2/5 gap-2 md:gap-3">
                    <img
                      src={propertyData?.images?.[0]?.image ?? StaysEmptyState}
                      alt="Stay image"
                      className="rounded-lg w-full max-w-[160px] md:max-w-[180px] mb-2"
                    />
                    <div className="font-semibold text-lg mb-1">
                      Prepare for your stay
                    </div>
                    <p className="text-sm text-gray-700 mb-2">
                      All guests are required to complete verification prior to
                      check-in. Follow the link below to get started on
                      verification. After completion you'll be forwarded to your
                      personalized check-in page with all the details about the
                      property.
                    </p>
                    {reservationQuery.data?.preCheckUrl ? (
                      <a
                        href={reservationQuery.data.preCheckUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary font-semibold w-fit"
                      >
                        complete your reservation
                      </a>
                    ) : (
                      <button
                        className="btn btn-disabled font-semibold w-fit"
                        disabled
                      >
                        complete your reservation
                      </button>
                    )}
                  </div>
                )}
                {!isPastReservation && (
                  <>
                    <div className="my-4 w-full h-px bg-gray-200 md:hidden" />
                    <div
                      className="
                        hidden
                        md:block
                        md:mt-4
                        md:h-[360px]
                        md:w-px
                        md:bg-gray-200
                        md:rounded-full
                        self-start
                      "
                    />
                  </>
                )}
                <div
                  className={`flex flex-col gap-4 p-2 md:p-4 self-start ${
                    isPastReservation ? 'md:w-full' : 'md:w-3/5'
                  }`}
                >
                  <div className="grid grid-cols-[140px_1fr] gap-x-24 gap-y-6 text-base">
                    <div className="font-bold whitespace-nowrap">Check-in</div>
                    <div className="text-base">
                      {propertyData?.checkinDate
                        ? formatDayAndDateTime(propertyData?.checkinDate)
                        : formatDayAndDateTime(
                            reservationQuery.data.checkinDate
                          )}
                    </div>
                    <div className="font-bold whitespace-nowrap">Check-out</div>
                    <div className="text-base">
                      {propertyData?.checkoutDate
                        ? formatDayAndDateTime(propertyData?.checkoutDate)
                        : formatDayAndDateTime(
                            reservationQuery.data.checkoutDate
                          )}
                    </div>
                    <div className="font-bold whitespace-nowrap">
                      Destination
                    </div>
                    <div className="text-base">
                      <a
                        href={
                          reservationQuery.data.gmapsLocation ||
                          `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            reservationQuery.data.propertyAddress
                          )}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-700 hover:underline"
                        onClick={e => {
                          e.preventDefault()
                          const token = getUserToken()
                          const user = token ? getUser(token) : null
                          const destinationUrl =
                            reservationQuery.data.gmapsLocation ||
                            `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              reservationQuery.data.propertyAddress
                            )}`
                          trackClick(
                            'property-destination-link',
                            destinationUrl,
                            user?.email
                          )
                          window.open(
                            destinationUrl,
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }}
                      >
                        {reservationQuery.data.propertyTitle}{' '}
                        <FontAwesomeIcon
                          icon={faArrowUpRightFromSquare}
                          className="text-cabana text-sm ml-1"
                          aria-label="Opens in new tab"
                        />
                      </a>
                      <br />
                      {reservationQuery.data.propertyAddress}
                    </div>
                    <div className="font-bold whitespace-nowrap">
                      Confirmation Code
                    </div>
                    <div className="text-base">
                      {reservationQuery.data.reservationId}
                    </div>
                    <div className="font-bold whitespace-nowrap">Unit Type</div>
                    <div className="text-base">
                      {reservationQuery.data.unitType}
                    </div>
                    <div className="font-bold whitespace-nowrap">
                      Cancellation Policy
                    </div>
                    <div className="text-base">
                      {(() => {
                        const rawPolicy =
                          propertyData?.cancellationPolicy ??
                          reservationQuery.data.cancellationPolicy

                        const policy = stripHtmlTags(rawPolicy || '')

                        const MAX_LENGTH = 112

                        if (policy && policy.length > MAX_LENGTH) {
                          return (
                            <div className="flex flex-col">
                              <span>{policy.substring(0, MAX_LENGTH)}...</span>
                              <button
                                ref={policyTriggerRef}
                                className="text-teal-700 hover:underline text-xs mt-1 text-left cursor-pointer w-fit"
                                onClick={e => {
                                  e.stopPropagation()
                                  const next = !showCancellationPolicyPopover
                                  setShowCancellationPolicyPopover(next)
                                  if (next) {
                                    setTimeout(
                                      () => updatePolicyPopoverPosition(),
                                      0
                                    )
                                  }
                                }}
                              >
                                See full policy
                              </button>
                            </div>
                          )
                        }
                        return policy
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </Accordion.Content>
          </section>
          <section
            className={`collapse collapse-arrow ${
              openSection === 'manage' ? 'collapse-open' : ''
            }`}
            aria-label="Manage Reservation Section"
          >
            <input
              type="checkbox"
              name="res-accordion"
              aria-controls="manage-reservation-accordion"
              checked={openSection === 'manage'}
              onChange={() =>
                setOpenSection(openSection === 'manage' ? null : 'manage')
              }
            />
            <Accordion.Title className="accordion-title collapse-arrow">
              Manage Reservation & Invoices
            </Accordion.Title>
            <Accordion.Content className="accordion-content">
              <div>
                <div className="w-full h-px bg-gray-200 mb-2" />
                {!isPastReservation && !isHiltonReservation && (
                  <div className="flex flex-col md:flex-row md:items-center md:gap-12 pt-4">
                    <div className="flex-1">
                      <h2 className="font-semibold text-lg mb-1">
                        Change Dates
                      </h2>
                      <p>
                        {isMonthlyStay
                          ? 'Date changes on monthly stays are handled by ' +
                            'our team — reach out and we’ll take care of it.'
                          : 'Extend your stay or adjust your dates. You’ll ' +
                            'see the updated price before anything changes.'}
                      </p>
                    </div>
                    {!isMonthlyStay && (
                      <div className="flex-1 flex justify-start md:justify-end gap-4 mt-5 md:mt-0 mb-4">
                        <Button
                          className="btn btn-secondary font-semibold w-fit px-8 py-2 min-w-[220px]"
                          onClick={() => {
                            const token = getUserToken()
                            const user = token ? getUser(token) : null
                            trackClick(
                              'modify-reservation-button',
                              `/member/stays/${reservationId}`,
                              user?.email
                            )
                            trackBookingManagement(
                              'modify_booking_initiated',
                              reservationId,
                              undefined,
                              user?.email
                            )

                            setShowModifyDialog(true)
                          }}
                          aria-label="Change reservation dates"
                        >
                          change dates
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                {!isPastReservation && (
                  <>
                    <div className="flex flex-col md:flex-row md:items-center md:gap-12 pt-4">
                      <div className="flex-1">
                        <h2 className="font-semibold text-lg mb-1">
                          Cancel Reservation
                        </h2>
                        <p>
                          Only reservations made at Placemakr.com can be
                          cancelled here.
                        </p>
                        <div className="pt-4 md:hidden">
                          <CancellationPolicy
                            reservation={reservationQuery.data}
                          />
                        </div>
                      </div>
                      <div className="flex-1 flex justify-start md:justify-end gap-4 mt-5 md:mt-0 mb-4">
                        <Button
                          className="btn btn-secondary font-semibold w-fit px-8 py-2 min-w-[220px]"
                          onClick={() => {
                            const token = getUserToken()
                            const user = token ? getUser(token) : null
                            trackClick(
                              'cancel-reservation-button',
                              `/member/stays/${reservationId}`,
                              user?.email
                            )
                            trackBookingManagement(
                              'cancel_booking_initiated',
                              reservationId,
                              undefined,
                              user?.email
                            )

                            setShowCancelDialog(true)
                          }}
                          aria-label="Cancel Reservation Button"
                        >
                          cancel reservation
                        </Button>
                      </div>
                    </div>
                    <div className="hidden md:block pt-5">
                      <CancellationPolicy reservation={reservationQuery.data} />
                    </div>
                  </>
                )}
                <div className="flex flex-col pt-1">
                  <DownloadSection
                    channel={reservationQuery.data.channel}
                    pmsSource={reservationQuery.data.pmsSource}
                    isHilton={reservationQuery.data.isHilton}
                    folios={foliosWithBillingPeriod}
                    isError={invoiceQuery.isError}
                    isLoading={receiptsLoading}
                    checkinDate={reservationQuery?.data?.checkinDate}
                    checkoutDate={reservationQuery?.data?.checkoutDate}
                    reservationId={reservationId}
                    isMonthlyStay={isMonthlyStay}
                    onDownloadSingle={handleDownloadSingleInvoice}
                    onDownloadAll={handleDownloadAllInvoices}
                    downloadingInvoiceId={downloadingInvoiceId}
                  />
                </div>
                <MultiInvoiceModal
                  isOpen={showMultiInvoiceModal}
                  invoiceCount={pendingDownloadFolios.length}
                  onClose={() => setShowMultiInvoiceModal(false)}
                  onConfirm={handleConfirmMultiDownload}
                />
                <ContactInfoFooter
                  propertyTitle={reservationQuery.data.propertyTitle}
                  propertyPhone={reservationQuery.data.propertyPhone}
                  analyticsContext="manage-reservation"
                />
              </div>
            </Accordion.Content>
          </section>

          {!isPastReservation &&
            isMonthlyStay &&
            (paymentScheduleQuery.isLoading ||
              paymentHistoryQuery.isLoading ||
              scheduleWithBillingPeriods.length > 0 ||
              paymentHistoryWithBillingPeriods.length > 0) && (
              <PaymentInstallmentScheduleSection
                isOpen={openSection === 'schedule'}
                onToggle={() =>
                  setOpenSection(openSection === 'schedule' ? null : 'schedule')
                }
                isLoading={
                  paymentScheduleQuery.isLoading ||
                  billingPeriodsQuery.isLoading ||
                  paymentHistoryQuery.isLoading
                }
                schedule={scheduleWithBillingPeriods}
                paymentHistory={paymentHistoryWithBillingPeriods}
                checkinDate={reservationQuery?.data?.checkinDate}
                checkoutDate={reservationQuery?.data?.checkoutDate}
                folios={invoiceQuery.data?.folios || []}
                foliosLoading={invoiceQuery.isLoading}
              />
            )}

          {!isPastReservation && (
            <section
              className={`collapse collapse-arrow ${
                openSection === 'payment' ? 'collapse-open' : ''
              }`}
              aria-label="Update Payment Section"
            >
              <input
                type="checkbox"
                name="res-accordion"
                aria-controls="manage-reservation-accordion"
                checked={openSection === 'payment'}
                onChange={() =>
                  setOpenSection(openSection === 'payment' ? null : 'payment')
                }
              />
              <Accordion.Title className="accordion-title">
                Update Payment
              </Accordion.Title>
              <Accordion.Content className="accordion-content">
                <div>
                  <div className="w-full h-px bg-gray-200 mb-4" />
                  <div>
                    <p className="py-4">
                      Update a payment method to save to your reservation. Your
                      payment method will not be charged at this time. If your
                      reservation wasn't prepaid, we will run your payment 1 day
                      before check-in.
                    </p>
                    <MemberUpdatePayment reservation={reservationQuery.data} />
                    <ContactInfoFooter
                      propertyTitle={reservationQuery.data.propertyTitle}
                      propertyPhone={reservationQuery.data.propertyPhone}
                      analyticsContext="update-payment"
                      linkClassName="text-cabana md:text-black underline hover:opacity-80"
                      containerClassName="pt-4"
                    />
                  </div>
                </div>
              </Accordion.Content>
            </section>
          )}

          <section
            className={`collapse collapse-arrow ${
              openSection === 'assistance' ? 'collapse-open' : ''
            }`}
            aria-label="Get Assistance Section"
          >
            <input
              type="checkbox"
              name="res-accordion"
              aria-controls="manage-reservation-accordion"
              checked={openSection === 'assistance'}
              onChange={() =>
                setOpenSection(
                  openSection === 'assistance' ? null : 'assistance'
                )
              }
            />
            <Accordion.Title className="accordion-title">
              Get assistance
            </Accordion.Title>
            <Accordion.Content className="accordion-content">
              <div>
                <div className="w-full h-px bg-gray-200 mb-4" />
                <p>Need a hand with something else? We're here for you 24/7.</p>
                <PropertyContactCard
                  propertyId={reservationQuery.data.propertyId}
                />
              </div>
            </Accordion.Content>
          </section>
        </div>
      ) : (
        ''
      )}

      {/* Cancellation Policy Popover */}
      {showCancellationPolicyPopover && (
        <div
          ref={policyPopoverRef}
          className="card compact bg-white border rounded-box z-50 max-w-sm shadow-lg fixed"
          style={{
            top: policyPopoverPosition.top,
            left: policyPopoverPosition.left
          }}
          role="dialog"
          aria-label="Cancellation policy details"
        >
          <div className="card-body">
            <div className="font-semibold text-base border-b mb-2">
              Cancellation Policy
            </div>
            <div className="text-sm max-h-64 overflow-y-auto pr-1 whitespace-pre-wrap">
              {reservationQuery.data &&
                stripHtmlTags(
                  propertyData?.cancellationPolicy ??
                    reservationQuery.data.cancellationPolicy
                )}
            </div>
          </div>
        </div>
      )}
    </main>
  )
}

export default ManageReservation
